import { scaleLinear, scalePow } from 'd3-scale';

import createUid from './util/uid';

class Axis {
	/**
	 * @param {Panel} panel - A reference to the panel this Axis belongs to
	 * @param {Object} scaleParams - Optional params used to configure the primary scale
	 */
	constructor(panel = {}, scaleParams = {}) {
		/** @type {Panel} */
		this.panel = panel;
		/** @type {Modcharts} */
		this.core = panel.core;
		/** @type {Map} */
		this.scales = new Map();
		this.primaryScaleUid = createUid();
		this.scales.set(
			this.primaryScaleUid,
			this.createScale(
				scaleParams.domain,
				scaleParams.scaleType,
				scaleParams.range
			)
		);
	}

	/**
	 * Adds new scale to the axis scale Map
	 * @param {d3-scale} newScale - The new scale to add to this axis
	 * @return {string} UID for the new scale
	 */
	addScale(newScale = this.createScale()) {
		const newUid = createUid();
		this.scales.set(newUid, newScale);
		return newUid;
	}

	/**
	 * Create and return a scale per the given parameters
	 * @param {Number[]} domain - Number array containing the domain bounds for the scale
	 * @param {('linear'|'log')} scaleType - The type of scale, linear or log
     * @param {Number[]} range - Number array containing the range bounds for the scale
	 * @returns {d3-scale} The newly created scale
	 */
	createScale(domain = [0, 0], scaleType = 'linear', range = [0, 0]) {
		const scale = scaleType === 'log'
			? scalePow().exponent(0.00001)
			: scaleLinear();
		scale.domain(domain).range(range);

		return scale;
	}

	/**
	 * Gets the axis scale with the given UID
	 * @param {string} scaleUid - The UID for the scale to get
	 * @return {d3-scale} The requested scale
	 */
	getScale(scaleUid) {
		return this.scales.get(scaleUid);
	}

	set primaryScale(newScale) {
		this.scales.set(this.primaryScaleUid, newScale);
	}

	get primaryScale() {
		return this.scales.get(this.primaryScaleUid);
	}

	/** Draws axis tool on the canvas  */
	render() {
		throw new Error('Required Axis render method not found');
	}

	/**
	 * Sets the range (min and max) for the axis scale with the given UID.
	 * The YAxis starts at the max and stops at the min. The XAxis starts at the min and
	 * stops at the max.
	 * @param {number} start - Value where the range starts
	 * @param {number} stop - Value where the range stops
	 */
	setScaleRange({
		scaleUid = this.primaryScaleUid,
		start,
		stop
	}) {
		this.scales.get(scaleUid).range([start, stop]);
	}

	/**
	 * Sets the domain (start and stop) for the axis scale with the given UID.
	 * The YAxis starts at the max and stops at the min. The XAxis starts at the min and
	 * stops at the max.
	 * @param {number} start - Value where the domain starts
	 * @param {number} stop - Value where the domain stops
	 * @param {boolean} shouldSetNice - round the domain to ‘nice’ round values
	 */
	setScaleDomain({
		start,
		stop,
		scaleUid = this.primaryScaleUid,
		shouldSetNice = false
	}) {
		const scale = this.scales.get(scaleUid);
		if (shouldSetNice) scale.nice();
		scale.domain([start, stop]);
	}

}

export default Axis;
